/** Reservations, availability, counting and replenishment. Slice 7. */
import { z } from 'zod';
import { countStatusSchema, countTypeSchema, countVarianceTypeSchema, reservationModeSchema, reservationStatusSchema, stockStateSchema, suggestionStatusSchema, } from './enums.js';
import { decimalIn, decimalOut, isoDateSchema, isoDateTimeSchema, nullableDecimalOut, readableSchema, uuidSchema, } from './primitives.js';
// --------------------------------------------------------------- availability
/**
 * The availability view. **Four distinct numbers, and a skin must not collapse any of
 * them into another.**
 *
 *   `on_hand`               what the ledger says is physically there
 *   `reserved_hard`         promised to a specific document; refuses an issue
 *   `reserved_soft`         advisory; visible to planning, blocks nothing
 *   `issuable`              `on_hand - reserved_hard` — what the posting path enforces
 *   `available_to_promise`  `on_hand - reserved_hard - reserved_soft` — what planning reads
 *
 * The last two differ on purpose (D-31). Showing only ATP makes a soft hold look like it
 * blocks, which is the one thing soft means it must not do. Showing only `issuable` makes
 * hard holds invisible to planning. Showing one number labelled "available" is how ICRM
 * ended up computing `available_stock = current_stock - reserved_stock` from two columns
 * nothing writes.
 *
 * `issuable` can be negative. That is not a bug: a count write-off posts through a hard
 * reservation deliberately — stock that is missing is missing whether or not it was spoken
 * for — and the resulting over-commitment surfaces here rather than being hidden.
 */
export const availabilitySchema = z.object({
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    stock_state: stockStateSchema,
    on_hand: decimalOut,
    reserved_hard: decimalOut,
    reserved_soft: decimalOut,
    issuable: decimalOut,
    available_to_promise: decimalOut,
});
// --------------------------------------------------------------- reservations
export const reservationSchema = readableSchema.extend({
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    stock_state: stockStateSchema,
    batch_id: uuidSchema.nullable().default(null),
    mode: reservationModeSchema,
    quantity: decimalOut,
    consumed_quantity: decimalOut,
    released_quantity: decimalOut,
    status: reservationStatusSchema,
    expires_at: isoDateTimeSchema.nullable().default(null),
    reference_type: z.string().nullable().default(null),
    reference_id: uuidSchema.nullable().default(null),
    reserved_by: uuidSchema.nullable().default(null),
    notes: z.string().nullable().default(null),
});
/** Derived, never stored — a remaining column would be a second source of truth. */
export function remainingQuantity(reservation) {
    return reservation.quantity.sub(reservation.consumed_quantity).sub(reservation.released_quantity);
}
export const reservationCreateSchema = z.object({
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    quantity: decimalIn({ gt: '0', label: 'quantity' }),
    mode: reservationModeSchema.default('HARD'),
    stock_state: stockStateSchema.default('AVAILABLE'),
    batch_id: uuidSchema.nullish(),
    /** Lapsed holds are swept inside the posting lock before availability is evaluated, so
     *  expiry is exact rather than eventual. No sweeper has to have run. */
    expires_at: isoDateTimeSchema.nullish(),
    reference_type: z.string().max(32).nullish(),
    reference_id: uuidSchema.nullish(),
    notes: z.string().nullish(),
});
export const reservationReleaseSchema = z.object({
    /** Omit to release whatever remains. */
    quantity: decimalIn({ gt: '0', label: 'quantity' }).nullish(),
    reason: z.string().max(2000).nullish(),
});
export const releasedCountSchema = z.object({ released: z.number().int() });
// -------------------------------------------------------------------- counting
export const countLineSchema = readableSchema.extend({
    session_id: uuidSchema,
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    bin_id: uuidSchema.nullable().default(null),
    batch_id: uuidSchema.nullable().default(null),
    stock_state: stockStateSchema,
    /** Withheld by the server while a blind session is still being counted. A skin must
     *  render the absence as "blind", never as zero. */
    expected_quantity: nullableDecimalOut.default(null),
    counted_quantity: nullableDecimalOut.default(null),
    variance_type: countVarianceTypeSchema,
    recount_count: z.number().int(),
    counted_at: isoDateTimeSchema.nullable().default(null),
    counted_by: uuidSchema.nullable().default(null),
    /** The ledger entry that corrected this line. The loop Assets leaves open: its
     *  `approve_session` writes a signature and changes no record. */
    movement_id: uuidSchema.nullable().default(null),
    remarks: z.string().nullable().default(null),
});
export function varianceQuantity(line) {
    if (line.counted_quantity === null || line.expected_quantity === null)
        return null;
    return line.counted_quantity.sub(line.expected_quantity);
}
export const countSessionSchema = readableSchema.extend({
    number: z.string(),
    count_type: countTypeSchema,
    warehouse_id: uuidSchema,
    zone_id: uuidSchema.nullable().default(null),
    item_group_id: uuidSchema.nullable().default(null),
    count_date: isoDateSchema,
    status: countStatusSchema,
    blind: z.boolean(),
    frozen_at: isoDateTimeSchema.nullable().default(null),
    submitted_at: isoDateTimeSchema.nullable().default(null),
    submitted_by: uuidSchema.nullable().default(null),
    posted_at: isoDateTimeSchema.nullable().default(null),
    manifest_hash: z.string().nullable().default(null),
    signature_meaning: z.string().nullable().default(null),
    counted_by_name: z.string().nullable().default(null),
    notes: z.string().nullable().default(null),
    lines: z.array(countLineSchema).default([]),
});
export const countSessionCreateSchema = z.object({
    warehouse_id: uuidSchema,
    count_date: isoDateSchema,
    count_type: countTypeSchema.default('CYCLE'),
    zone_id: uuidSchema.nullish(),
    item_group_id: uuidSchema.nullish(),
    blind: z.boolean().default(true),
    counted_by_name: z.string().max(255).nullish(),
    notes: z.string().nullish(),
    number: z.string().max(40).nullish(),
});
export const countEntrySchema = z.object({
    line_id: uuidSchema,
    counted_quantity: decimalIn({ gte: '0', label: 'counted quantity' }),
    remarks: z.string().max(2000).nullish(),
});
export const countEntriesSchema = z.object({
    entries: z.array(countEntrySchema).min(1, 'record at least one counted line'),
});
export const countSubmitSchema = z.object({
    signature_meaning: z.string().max(120).nullish(),
});
/**
 * Recompute the signed manifest and compare.
 *
 * Ported whole from Inaivia Assets — the one control in the estate worth copying without
 * change. Any edit to a counted line after signing flips `valid` to false.
 */
export const manifestVerificationSchema = z.object({
    session_id: uuidSchema,
    valid: z.boolean(),
    stored_hash: z.string().nullable().default(null),
    computed_hash: z.string(),
    signature_meaning: z.string().nullable().default(null),
});
export const countSummarySchema = z.object({
    session_id: uuidSchema,
    total_lines: z.number().int(),
    counted_lines: z.number().int(),
    match: z.number().int(),
    short: z.number().int(),
    over: z.number().int(),
    not_counted: z.number().int(),
    net_variance: decimalOut,
});
// ------------------------------------------------------ reorder & replenishment
export const reorderRuleSchema = readableSchema.extend({
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    reorder_point: decimalOut,
    reorder_quantity: decimalOut,
    minimum_quantity: decimalOut,
    maximum_quantity: nullableDecimalOut.default(null),
    preferred_vendor_id: uuidSchema.nullable().default(null),
    lead_time_days: z.number().int().nullable().default(null),
    active: z.boolean(),
});
export const reorderRuleUpsertSchema = z.object({
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    reorder_point: decimalIn({ gte: '0', label: 'reorder point' }).default('0'),
    reorder_quantity: decimalIn({ gte: '0', label: 'reorder quantity' }).default('0'),
    minimum_quantity: decimalIn({ gte: '0', label: 'minimum quantity' }).default('0'),
    maximum_quantity: decimalIn({ gte: '0', label: 'maximum quantity' }).nullish(),
    preferred_vendor_id: uuidSchema.nullish(),
    lead_time_days: z.number().int().min(0).nullish(),
    active: z.boolean().default(true),
});
/**
 * A shortfall with its whole derivation attached.
 *
 * Accounts' equivalent is a `below_reorder` boolean — true or false with nothing behind it
 * and nowhere to go. Snapshotting the arithmetic is what lets the person approving the
 * resulting requisition see how the number was reached, so a skin should show the working,
 * not just `suggested_quantity`.
 */
export const suggestionSchema = readableSchema.extend({
    run_id: uuidSchema,
    item_id: uuidSchema,
    warehouse_id: uuidSchema,
    on_hand_quantity: decimalOut,
    reserved_quantity: decimalOut,
    /** Derived from receipt lines, not from a counter — `purchase_order_lines` deliberately
     *  has no `received_quantity`, so the figure cannot drift. */
    on_order_quantity: decimalOut,
    net_available: decimalOut,
    reorder_point: decimalOut,
    suggested_quantity: decimalOut,
    vendor_id: uuidSchema.nullable().default(null),
    status: suggestionStatusSchema,
    requisition_id: uuidSchema.nullable().default(null),
});
export const replenishmentRunSchema = readableSchema.extend({
    number: z.string(),
    run_date: isoDateSchema,
    warehouse_id: uuidSchema.nullable().default(null),
    evaluated_count: z.number().int(),
    suggestion_count: z.number().int(),
    run_by: uuidSchema.nullable().default(null),
    suggestions: z.array(suggestionSchema).default([]),
});
export const replenishmentRunCreateSchema = z.object({
    run_date: isoDateSchema,
    warehouse_id: uuidSchema.nullish(),
    number: z.string().max(40).nullish(),
});
/** A person converts. The run itself never orders (D-34). */
export const suggestionConvertSchema = z.object({
    suggestion_ids: z.array(uuidSchema).min(1, 'select at least one suggestion'),
    request_date: isoDateSchema.nullish(),
    notes: z.string().nullish(),
});
/**
 * The requisition a conversion produces.
 *
 * Only the fields a replenishment screen needs are modelled; procurement proper is out of
 * scope for this skin and `.passthrough()` keeps the rest of the document intact rather
 * than dropping it on the floor.
 */
export const requisitionSchema = readableSchema
    .extend({
    number: z.string(),
    status: z.string(),
    request_date: isoDateSchema.nullable().default(null),
})
    .passthrough();
//# sourceMappingURL=planning.js.map